import { useTranslation } from 'react-i18next';
import { FilterConfig, FilterType } from '../../types/header-toolbar.type';
import { SelectFilter, UserFilter } from '../common-filters';
import { DateRangeFilter } from 'components/blocks/data-table/data-table-date-filter';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';

interface FilterControlsProps<T extends FilterType> {
  filters: T;
  filterConfigs: FilterConfig[];
  onFiltersChange: (filters: T) => void;
  isMobile?: boolean;
}

const TOOLBAR_TRIGGER_CLASS_NAME =
  'h-8 rounded-lg border border-[#e7e7e7] bg-background px-3 text-primary-600 dark:border-grey-400 dark:text-[#ffffff]';

const LIBRARY_TOOLBAR_TRIGGER_CLASS_NAME =
  '!h-9 w-[140px] justify-between rounded-[8px] border border-[#e7e7e7] bg-white px-2 text-[14px] font-normal text-[#3d3d3d] shadow-none dark:border-grey-400 dark:bg-card dark:text-foreground';

export const FilterControls = <T extends FilterType>({
  filters,
  filterConfigs,
  onFiltersChange,
  isMobile = false,
}: FilterControlsProps<T>) => {
  const { t } = useTranslation();
  const isMediaLibrary = useIsMediaLibrary();
  const triggerClassName = isMediaLibrary ? LIBRARY_TOOLBAR_TRIGGER_CLASS_NAME : TOOLBAR_TRIGGER_CLASS_NAME;

  const handleFilterChange = (key: string, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    } as T);
  };

  const visibleConfigs = filterConfigs.filter((config) =>
    isMobile ? config.showInMobile !== false : config.showInDesktop !== false
  );

  return (
    <div className={isMobile ? 'space-y-4' : isMediaLibrary ? 'flex shrink-0 items-center gap-4' : 'flex shrink-0 items-center gap-2'}>
      {visibleConfigs.map((config) => {
        const commonProps = {
          mode: 'range',
          key: config.key,
          title: config.label,
          className: isMobile ? 'w-full' : config.width,
        };

        if (isMobile) {
          return (
            <div key={config.key} className="w-full">
              <label className="text-sm font-medium block mb-2">{t(config.label)}</label>
              {renderFilterComponent(config, filters, handleFilterChange, commonProps, isMediaLibrary, triggerClassName)}
            </div>
          );
        }

        return (
          <div key={config.key} className={isMediaLibrary ? 'flex h-9 shrink-0 items-center' : 'flex h-8 shrink-0 items-center'}>
            {renderFilterComponent(config, filters, handleFilterChange, commonProps, isMediaLibrary, triggerClassName)}
          </div>
        );
      })}
    </div>
  );
};

const renderFilterComponent = (
  config: FilterConfig,
  filters: FilterType,
  handleFilterChange: (key: string, value: any) => void,
  commonProps: any,
  isMediaLibrary: boolean,
  triggerClassName: string
) => {
  const filterValue = (filters as any)[config.key];

  switch (config.type) {
    case 'select':
      return (
        <SelectFilter
          {...commonProps}
          value={filterValue}
          onValueChange={(value) => {
            const newValue = value === 'all' ? undefined : value;
            handleFilterChange(config.key, newValue);
          }}
          options={config.options || []}
          allLabel="ALL_TYPES"
          hideLeadingIcon={isMediaLibrary}
          className={`focus:outline-none focus:ring-0 ${isMediaLibrary ? triggerClassName : `w-[130px] ${TOOLBAR_TRIGGER_CLASS_NAME}`}`}
        />
      );

    case 'dateRange':
      return (
        <DateRangeFilter
          {...commonProps}
          date={filterValue}
          onDateChange={(dateRange) => handleFilterChange(config.key, dateRange)}
          triggerClassName={triggerClassName}
          forceOutline
          iconPosition={isMediaLibrary ? 'end' : 'start'}
        />
      )

    case 'user':
      return (
        <UserFilter
          {...commonProps}
          value={filterValue}
          onValueChange={(userId) => handleFilterChange(config.key, userId)}
          users={config.users || []}
        />
      );

    default:
      return null;
  }
};
